import { useMemo, useState } from 'react';
import { Badge, Button, Group, Loader, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { Icon, OrganizationAdminModal } from '@/components';
import {
  ADMIN_PROGRAM_LABELS,
  CHURCH_ADMIN_PROGRAM,
  type ChurchAdmin,
  TAB_TO_PROGRAM,
} from '@/types/users';
import usersService from '@/services/users.service';
import styles from './OrganizationAdmins.module.sass';
import { useParams } from 'react-router-dom';
import { CUSTOM_ICONS } from '@/types/enums';
import type { IconName } from '@/components/Icon';
import { useTabStatus } from '@/hooks/useTabStatus';
import { ORGANIZATION_TAB } from '@/types/organizationPrograms';
import { showError } from '@/shared';
import { cognitoUserManager } from '@/config/authConfig';
import { buildMpaTokenFragmentFromOidcUser, redirectToMpaHandoff } from '@/helpers';
import { useInitTempChurchAdminAccessMutation } from '@/hooks';
import {
  useAddChurchAdminMutation,
  useChurchAdmins,
  useRemoveChurchAdminMutation,
  useUpdateChurchAdminMutation,
} from '@/hooks/churches';
import { CHURCH_ADMIN_ACCESS_PRODUCT } from '@/types';

const LAYOUT = {
  EDIT_ICON_SIZE: 16,
  TABLE_PROGRAM_ICON_SIZE: 16,
  HEADER_BUTTON_ICON_SIZE: 16,
};
const MPA_LOGIN_URL = import.meta.env.VITE_MPA_LOGIN_URL;

const PROGRAM_TO_ACCESS_PRODUCT: Partial<Record<CHURCH_ADMIN_PROGRAM, CHURCH_ADMIN_ACCESS_PRODUCT>> = {
  [CHURCH_ADMIN_PROGRAM.MPA]: CHURCH_ADMIN_ACCESS_PRODUCT.MPA,
  [CHURCH_ADMIN_PROGRAM.MESSAGES]: CHURCH_ADMIN_ACCESS_PRODUCT.MESSAGES,
  [CHURCH_ADMIN_PROGRAM.CAMINO]: CHURCH_ADMIN_ACCESS_PRODUCT.CAMINO,
  [CHURCH_ADMIN_PROGRAM.FORMS]: CHURCH_ADMIN_ACCESS_PRODUCT.FORMS,
};

const toChurchAdminAccessPrograms = (programs: CHURCH_ADMIN_PROGRAM[]): CHURCH_ADMIN_ACCESS_PRODUCT[] =>
  programs.flatMap((program) => {
    const accessProduct = PROGRAM_TO_ACCESS_PRODUCT[program];
    return accessProduct ? [accessProduct] : [];
  });

type OrganizationAdminFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  programs: CHURCH_ADMIN_PROGRAM[];
  userId: string | null;
  ssoUserId: string | null;
};

const getProgramIcon = (program: CHURCH_ADMIN_PROGRAM) => {
  const iconMap: Partial<Record<CHURCH_ADMIN_PROGRAM, IconName>> = {
    mpa: CUSTOM_ICONS.MY_PARISH,
    camino: CUSTOM_ICONS.CAMINO,
    mio: CUSTOM_ICONS.MASS_INTENTIONS,
    [CHURCH_ADMIN_PROGRAM.MINISTRY_SCHEDULER]: CUSTOM_ICONS.FACILITIES,
    adoration: CUSTOM_ICONS.ADORATION,
    messages: 'IconMessageFilled',
    forms: CUSTOM_ICONS.FORMS,
  };
  return iconMap[program];
};

export const OrganizationAdmins = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editAdmin, setEditAdmin] = useState<ChurchAdmin | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { id: churchId } = useParams<{ id: string }>();
  const { tabStatus } = useTabStatus();

  const { data: admins = [], isLoading } = useChurchAdmins(churchId);
  const addAdminMutation = useAddChurchAdminMutation(churchId ?? '');
  const updateAdminMutation = useUpdateChurchAdminMutation(churchId ?? '');
  const removeAdminMutation = useRemoveChurchAdminMutation(churchId ?? '');
  const initTempAccessMutation = useInitTempChurchAdminAccessMutation();

  const enabledPrograms = useMemo(() => {
    const programs: CHURCH_ADMIN_PROGRAM[] = [];
    Object.entries(tabStatus).forEach(([tab, isActive]) => {
      if (tab === ORGANIZATION_TAB.ORG_ADMINS) {
        return;
      }
      if (isActive && tab in TAB_TO_PROGRAM) {
        const program = TAB_TO_PROGRAM[tab as ORGANIZATION_TAB];
        if (program && !programs.includes(program)) {
          programs.push(program);
        }
      }
    });
    return programs;
  }, [tabStatus]);

  const handleAddAdminClick = () => {
    setEditAdmin(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleEditAdminClick = (userId: string) => {
    const admin = admins.find((a) => a.userId === userId);
    if (!admin) return;
    setEditAdmin(admin);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditAdmin(null);
    setIsEditMode(false);
  };

  const handleModalSave = async (args: OrganizationAdminFormValues) => {
    if (!churchId) return;

    try {
      if (args.userId) {
        await updateAdminMutation.mutateAsync({
          userId: args.userId,
          admin: {
            email: args.email,
            firstName: args.firstName,
            lastName: args.lastName,
            programs: args.programs,
          },
        });
      } else {
        await addAdminMutation.mutateAsync({
          email: args.email,
          firstName: args.firstName,
          lastName: args.lastName,
          programs: args.programs,
          ssoUserId: args.ssoUserId,
        });
      }
    } catch (error) {
      console.error(error);
      throw error;
    }

    setIsModalOpen(false);
    setEditAdmin(null);
    setIsEditMode(false);
  };

  const handleModalRemove = async () => {
    if (!churchId || !editAdmin) return;
    await removeAdminMutation.mutateAsync(editAdmin.userId);
    setIsModalOpen(false);
    setEditAdmin(null);
    setIsEditMode(false);
  };

  const handleEmailLookup = async (email: string) => {
    return await usersService.lookupByEmail(email);
  };

  const handleEnterOrgClick = async () => {
    try {
      if (!churchId) {
        showError('Missing organization id');
        return;
      }

      if (!MPA_LOGIN_URL) {
        showError('Missing VITE_MPA_LOGIN_URL configuration');
        return;
      }

      const programs = toChurchAdminAccessPrograms(enabledPrograms);
      if (programs.length === 0) {
        showError('No supported programs are enabled for this organization');
        return;
      }

      const user = await cognitoUserManager.getUser();
      if (!user?.access_token) {
        showError('No active session found');
        return;
      }

      const tokenFragment = buildMpaTokenFragmentFromOidcUser(user);
      if (!tokenFragment) {
        showError('Session is missing tokens required for impersonation handoff');
        return;
      }

      const { email } = await initTempAccessMutation.mutateAsync({ churchId, programs });
      if (!email) {
        showError('Missing overall admin email for impersonation');
        return;
      }

      redirectToMpaHandoff({
        loginUrl: MPA_LOGIN_URL,
        tokenFragment,
        state: {
          'X-Impersonate-User': email,
          impersonatedUserName: email,
          church_id: churchId,
          returnToControlCenterUrl: window.location.href,
          isOverallAdmin: 'true',
        },
      });
    } catch (error) {
      showError(error);
    }
  };

  const handleImpersonateAdminClick = async ({
    email,
    impersonatedUserName,
  }: {
    email: string;
    impersonatedUserName: string;
  }) => {
    try {
      if (!email) {
        showError('Missing user email for impersonation');
        return;
      }

      if (!MPA_LOGIN_URL) {
        showError('Missing VITE_MPA_LOGIN_URL configuration');
        return;
      }

      const user = await cognitoUserManager.getUser();
      if (!user?.access_token) {
        showError('No active session found');
        return;
      }

      const tokenFragment = buildMpaTokenFragmentFromOidcUser(user);
      if (!tokenFragment) {
        showError('Session is missing tokens required for impersonation handoff');
        return;
      }

      redirectToMpaHandoff({
        loginUrl: MPA_LOGIN_URL,
        tokenFragment,
        state: {
          'X-Impersonate-User': email,
          impersonatedUserName,
          church_id: churchId ?? '',
          returnToControlCenterUrl: window.location.href,
        },
      });
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Paper p="xl" radius="md" w="100%">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Title order={3}>Organization Admins</Title>
          <Group justify="center" p="md">
            <Button
              size="sm"
              onClick={handleEnterOrgClick}
              variant="default"
              loading={initTempAccessMutation.isPending}
              leftSection={<Icon name="IconLogin2" size={LAYOUT.HEADER_BUTTON_ICON_SIZE} />}
            >
              Enter Org
            </Button>
            <Button
              size="sm"
              onClick={handleAddAdminClick}
              variant="default"
              leftSection={<Icon name="IconUserPlus" size={LAYOUT.HEADER_BUTTON_ICON_SIZE} />}
            >
              Add Admin
            </Button>
          </Group>
        </Group>

        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading admins...</Text>
          </Stack>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Programs</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {admins.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" c="dimmed">
                      No admins found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                admins.map((admin) => (
                  <Table.Tr key={admin.userId || admin.email || `admin-${Math.random()}`}>
                    <Table.Td>
                      <Text className={styles.adminName}>
                        {admin.firstName && admin.lastName
                          ? `${admin.firstName} ${admin.lastName}`
                          : admin.firstName || admin.lastName || 'N/A'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text className={styles.adminEmail}>{admin.email || 'N/A'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {admin.programs && admin.programs.length > 0 ? (
                        <Group gap="xs">
                          {admin.programs.map((program: CHURCH_ADMIN_PROGRAM) => (
                            <Badge
                              key={program}
                              variant="default"
                              color="black"
                              leftSection={
                                <Icon
                                  name={getProgramIcon(program) as IconName}
                                  size={LAYOUT.TABLE_PROGRAM_ICON_SIZE}
                                />
                              }
                            >
                              {ADMIN_PROGRAM_LABELS[program]}
                            </Badge>
                          ))}
                        </Group>
                      ) : (
                        <Text className={styles.adminApps}>N/A</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        {admin.canBeImpersonated && admin.ssoUserId ? (
                          <Button
                            variant="default"
                            size="xs"
                            onClick={() =>
                              handleImpersonateAdminClick({
                                email: admin.email ?? '',
                                impersonatedUserName:
                                  `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || admin.email || '',
                              })
                            }
                          >
                            Impersonate
                          </Button>
                        ) : null}
                        <Button
                          variant="subtle"
                          size="xs"
                          onClick={() => handleEditAdminClick(admin.userId || '')}
                          p={0}
                        >
                          <Icon name="IconEdit" size={LAYOUT.EDIT_ICON_SIZE} color="black" />
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
        <OrganizationAdminModal
          opened={isModalOpen}
          isEditMode={isEditMode}
          initialAdmin={editAdmin}
          onClose={handleModalClose}
          onSave={handleModalSave}
          onRemove={handleModalRemove}
          enabledPrograms={enabledPrograms}
          onEmailLookup={handleEmailLookup}
        />
      </Stack>
    </Paper>
  );
};
